'use client';

import { useSearchParams, useParams } from 'next/navigation';
import { useRef, useState, useCallback, useEffect, useMemo } from 'react';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import MuiButton from '@mui/material/Button';
import { styled } from '@mui/material/styles';
import { builder, BuilderBlocks } from '@builder.io/react';
import { useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';

import useIsMobile from 'hooks/useIsMobile';
import useLoginModal from 'hooks/useLoginModal';
import { COLORS } from '../../designTokens';
import { sectionBorderRadiusSx } from '../shared/sectionBorderRadius';
import { isTtwOffering } from '../shared/offeringStatus';
import useIsHydrated from '../shared/useIsHydrated';
import useIsEditorialReskin from '../shared/useIsEditorialReskin';
import { getOffering } from 'services/offerings';
import useGetRewards from 'hooks/useGetRewards';
import { formatCurrencyFlexible, getEnv } from 'utils/helpers';
import { INVESTOR_SIGNALS_BEHAVIORAL_EVENTS } from 'utils/investorSignalsBehavioralProperties';
import { isOverAmountRaised, resolveOfferingInvestCtaText } from 'utils/offerings/helpers';
import { investNowClick, trackEvent, trackSEEvent, ADD_TO_CART_SURFACES } from 'utils/analytics';
import { userMenu } from 'utils/links';
import AllRewardsModal from 'components/Offering/Rewards/AllRewardsModal';
import useOfferingDaysLeft from 'hooks/useOfferingDaysLeft';
import BuilderClassicInvestSidebar from '../BuilderClassicInvestSidebar';

const LayoutContainer = styled(Box)(() => ({
  width: '100%',
  boxSizing: 'border-box',
}));

const ContentWrapper = styled(Box)(({ theme }) => ({
  maxWidth: '1280px',
  margin: '20px auto 100px auto',
  width: '100%',
  padding: '0 16px',
  boxSizing: 'border-box',
  [theme.breakpoints.up('md')]: {
    padding: '0 24px',
  },
}));

const LeftColumn = styled(Grid)(() => ({
  maxWidth: '100%',
  overflowX: 'hidden',
  '& .builder-blocks, & .builder-block': {
    maxWidth: '100% !important',
    minWidth: '0 !important',
  },
}));

const RightColumn = styled(Grid)(({ theme }) => ({
  position: 'relative',
  [theme.breakpoints.down('lg')]: {
    display: 'none',
  },
}));

const _StickyWrapper = styled(Box)(() => ({
  zIndex: 999,
  width: '369px',
  paddingRight: '1px',
}));

const MobileBottomBar = styled(Box)(({ theme }) => ({
  position: 'fixed',
  bottom: 0,
  left: 0,
  right: 0,
  zIndex: 1100,
  backgroundColor: '#FFFFFF',
  borderTop: '1px solid #E5E7EB',
  padding: '8px 16px',
  display: 'none',
  [theme.breakpoints.down('lg')]: {
    display: 'block',
  },
  [theme.breakpoints.down('sm')]: {
    padding: '8px 12px',
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: '-100px',
    left: 0,
    right: 0,
    height: '100px',
    backgroundColor: 'inherit',
  },
}));

// `not_submitted` used to live here (no button at all on mobile for
// pre-Form-C offerings). It's now allowed through so that issuer-
// driven `forceReserveCta` overrides surface a Reserve Now CTA on the
// mobile bar in parity with the desktop sidebar; the regular green
// "Get Equity" CTA also renders for pre-launch drafts (status =
// not_submitted, `forceReserveCta` off) — `handleInvestClick` blocks
// the click so admins can preview the layout without it routing to
// the invest funnel.
const HIDE_BUTTON_STATUSES = new Set(['closed', 'completed']);

const MobileBarContent = ({ investButtonColor, waitlistButtonColor, forceReserveCta }) => {
  const params = useParams();
  const slug = params?.slug?.[0];
  const user = useSelector((state) => state.user);
  const investmentAccountStatus = useSelector((state) => state.user.investmentAccountStatus);
  const [renderLoginModal, setLoginModalOpen] = useLoginModal();
  const [rewardsModalOpen, setRewardsModalOpen] = useState(false);
  const isEditorialReskin = useIsEditorialReskin();

  const { data: offeringData } = useQuery({
    queryKey: ['offeringData', slug],
    queryFn: () => getOffering({ slug }),
    staleTime: 15 * 60 * 1000,
    enabled: Boolean(slug),
    placeholderData: (prev) => prev,
  });

  const { data: rewardData } = useGetRewards({ offeringId: offeringData?.id });
  const hasRewards = rewardData?.length > 0;
  const isOverRaised = isOverAmountRaised(offeringData) || offeringData?.lockedOnWaitlist;
  const _isRegD =
    offeringData?.regulationType === 'Reg D' || offeringData?.regulationType === '506c';
  const [getTillOfferingClose] = useOfferingDaysLeft();
  const endDays = useMemo(
    () => (offeringData ? getTillOfferingClose(offeringData) : null),
    [getTillOfferingClose, offeringData],
  );

  const pricePerShare = useMemo(() => {
    if (!offeringData?.pricePerShare) return '';
    return formatCurrencyFlexible(Number.parseFloat(offeringData.pricePerShare));
  }, [offeringData?.pricePerShare]);

  // `isTtwOffering` only auto-detects the four unambiguous V2 TTW
  // statuses and excludes Reg D — `not_submitted` is intentionally
  // *not* auto-TTW because the V2 payload can't distinguish a live
  // pre-Form-C TTW campaign from a draft preview (gacw-reg-a vs
  // heartstone-cf-2 are identical at the API level). The
  // `forceReserveCta` toggle on the parent two-column layout is the
  // explicit issuer/admin signal for those offerings; OR it in here
  // so the mobile bar's CTA matches the desktop sidebar's.
  const isTTW = isTtwOffering(offeringData) || Boolean(forceReserveCta);

  const buttonText = useMemo(() => {
    if (isOverRaised) return 'Join the Waitlist';
    if (isTTW) return 'Reserve Now';
    return resolveOfferingInvestCtaText({
      offeringData,
      configuredText: 'Get Shares Now',
    });
  }, [isOverRaised, isTTW, offeringData]);

  const showButton =
    Boolean(offeringData?.status) && !HIDE_BUTTON_STATUSES.has(offeringData.status);

  const getInvestUrl = useCallback(() => {
    const base = `${getEnv()}investment.startengine.com/${slug}`;
    // Use the local `isTTW` (status auto-detect OR `forceReserveCta`
    // override) so the URL stays in sync with the rendered button copy
    // — otherwise a forced Reserve Now button would silently route to
    // the regular invest funnel instead of `/reserve`.
    return isTTW ? `${base}/reserve` : base;
  }, [slug, isTTW]);

  const handleInvestClick = useCallback(async () => {
    // Mirror the bubble + hero carve-out: allow the click through for
    // live TTW campaigns (auto-detected or `forceReserveCta` on) and
    // block only true draft previews where there's no TTW intent.
    if (offeringData?.status === 'not_submitted' && !isTTW) return;
    if (investmentAccountStatus?.investmentAccountFrozen) {
      window.location.href = userMenu.portfolio;
      return;
    }
    const url = getInvestUrl();
    if (!user?.signedIn) {
      try {
        sessionStorage.setItem('builder_invest_redirect', url);
      } catch {
        /* private browsing */
      }
      setLoginModalOpen(true);
      return;
    }
    investNowClick({ offeringData, user, surface: ADD_TO_CART_SURFACES.CAMPAIGN_PAGE });
    await trackEvent(INVESTOR_SIGNALS_BEHAVIORAL_EVENTS.clickInvestNow, offeringData);
    await trackSEEvent(INVESTOR_SIGNALS_BEHAVIORAL_EVENTS.clickInvestNow, offeringData?.slug, user);
    window.location.href = url;
  }, [offeringData, user, investmentAccountStatus, setLoginModalOpen, getInvestUrl, isTTW]);

  const resolvedInvestColor = investButtonColor || COLORS.primary;
  const resolvedWaitlistColor = waitlistButtonColor || '#E06004';
  // TTW pages get the orange Reserve treatment to match the desktop sidebar's
  // hardcoded `#E06004` Reserve Now button — otherwise mobile users see teal
  // "Reserve Now" while desktop sees orange "Reserve Now" on the same offering.
  const resolvedReserveColor = '#E06004';
  const resolveButtonBackground = () => {
    if (isOverRaised) return resolvedWaitlistColor;
    if (isTTW) return resolvedReserveColor;
    // PD (Paige QA): the editorial mobile bottom bar's "Get Equity" must use the
    // deep editorial teal (seDark), not the Builder primary green.
    if (isEditorialReskin) return '#185C65';
    return resolvedInvestColor;
  };
  const buttonBackgroundColor = resolveButtonBackground();

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          fontFamily: "'IBM Plex Sans', sans-serif",
        }}
      >
        <Box sx={{ flexShrink: 0 }}>
          {pricePerShare && (
            <>
              <Typography
                sx={{ fontSize: '18px', fontWeight: 700, color: '#111827', lineHeight: 1.2 }}
              >
                {pricePerShare}
              </Typography>
              <Typography sx={{ fontSize: '13px', color: '#64748B', lineHeight: 1.3 }}>
                per share
              </Typography>
            </>
          )}
          {endDays && (
            <Typography
              sx={{
                fontSize: '12px',
                fontWeight: 700,
                color: '#9F1239',
                textTransform: 'uppercase',
                lineHeight: 1.3,
              }}
            >
              {endDays}
            </Typography>
          )}
          {hasRewards && offeringData?.status !== 'closed' && (
            <Typography
              onClick={() => setRewardsModalOpen(true)}
              sx={{
                fontSize: '13px',
                fontWeight: 700,
                color: COLORS.primary,
                textDecoration: 'underline',
                cursor: 'pointer',
                lineHeight: 1.3,
              }}
            >
              Show Rewards
            </Typography>
          )}
        </Box>
        {showButton && (
          <MuiButton
            onClick={handleInvestClick}
            variant="contained"
            sx={{
              flexShrink: 0,
              backgroundColor: buttonBackgroundColor,
              color: '#FFFFFF',
              borderRadius: '8px',
              textTransform: 'none',
              padding: '12px 32px',
              fontWeight: 600,
              fontSize: '15px',
              fontFamily: "'IBM Plex Sans', sans-serif",
              boxShadow: 'none',
              '&:hover': {
                backgroundColor: buttonBackgroundColor,
                opacity: 0.9,
                boxShadow: 'none',
              },
            }}
          >
            {buttonText}
          </MuiButton>
        )}
        {offeringData?.status === 'closed' && (
          <Box
            sx={{
              flexShrink: 0,
              backgroundColor: '#999',
              color: '#FFFFFF',
              borderRadius: '16px',
              padding: '12px 24px',
              fontWeight: 600,
              fontSize: '15px',
            }}
          >
            Offering Closed
          </Box>
        )}
      </Box>
      {rewardsModalOpen && (
        <AllRewardsModal open={rewardsModalOpen} handleClose={() => setRewardsModalOpen(false)} />
      )}
      {renderLoginModal({
        offering: offeringData,
        onSuccessfulLogin: () => {
          try {
            const savedUrl = sessionStorage.getItem('builder_invest_redirect');
            if (savedUrl) {
              sessionStorage.removeItem('builder_invest_redirect');
              window.location.href = savedUrl;
            }
          } catch {
            /* private browsing */
          }
        },
      })}
    </>
  );
};

const BuilderClassicTwoColumnLayout = ({
  sectionId = '',
  sectionBorderRadius = '0px',
  verticalPadding = 0,
  horizontalPadding = 0,
  maxWidth = 1280,
  backgroundColor = 'transparent',
  stickyTopOffset = 80,
  sidebarWidth = 369,
  leftColumnRatio = 7.8,
  rightColumnRatio = 4,
  showMobileBottomBar = true,
  showLoyaltyBonus = true,
  showStats = true,
  showRequestLiveStream = true,
  investButtonColor = '',
  waitlistButtonColor = '',
  forceReserveCta = false,
  builderBlock,
  leftContent,
}) => {
  const searchParams = useSearchParams();
  const isMobile = useIsMobile();
  const isEditorialReskin = useIsEditorialReskin();

  const _isBuilderEditing =
    builder?.isEditing ||
    builder?.isPreviewing ||
    searchParams?.get('__builder_editing__') === 'true' ||
    searchParams?.has('builder.frameEditing');

  const clampedMaxWidth = Math.min(1440, Math.max(1024, maxWidth || 1280));
  const rightColRef = useRef(null);
  const sidebarRef = useRef(null);
  const layoutRef = useRef(null);
  const isHydrated = useIsHydrated();
  const [isFooterInView, setIsFooterInView] = useState(false);

  // Builder.io's `.builder-block` wrapper around this layout sometimes
  // carries `overflow: hidden` from the editor's saved responsiveStyles
  // (or from a Builder.io SDK default for full-bleed sections). When any
  // ancestor's overflow is non-visible, the sidebar's `position: sticky`
  // pins to *that* ancestor — which doesn't itself scroll — so the
  // sidebar appears to ride away with the page instead of pinning. Walk
  // up to the nearest `.builder-block` and force overflow back to visible.
  // Restores the prior inline value on unmount so we don't poison any
  // siblings re-rendered into the same wrapper. Diagnostic that surfaced
  // this: console.table over ancestors flagged `.builder-block` with
  // `overflow: hidden` on `greenfield-robotics-rega` (May 2026).
  useEffect(() => {
    if (!isHydrated) return undefined;
    const layoutEl = layoutRef.current;
    if (!layoutEl) return undefined;
    // Local name avoids shadowing the `builderBlock` component prop (the
    // Builder.io block-data object passed at line ~302), which a future
    // maintainer might reach for inside this effect.
    const builderBlockEl = layoutEl.closest('.builder-block');
    if (!builderBlockEl) return undefined;
    const previousOverflow = builderBlockEl.style.overflow;
    builderBlockEl.style.overflow = 'visible';
    return () => {
      builderBlockEl.style.overflow = previousOverflow;
    };
  }, [isHydrated]);

  useEffect(() => {
    if (!isHydrated || !isEditorialReskin || !isMobile || typeof window === 'undefined') {
      setIsFooterInView(false);
      return undefined;
    }

    const legalSelectors = [
      'footer[data-cy="footer-section"]',
      '[data-cy="builder-footer-section"]',
      '[data-cy="builder-disclaimer"]',
    ];

    const syncFooterState = () => {
      const legalEls = legalSelectors.flatMap((selector) =>
        Array.from(document.querySelectorAll(selector)),
      );
      if (legalEls.length === 0) {
        setIsFooterInView(false);
        return;
      }

      const overlapsLegal = legalEls.some((el) => {
        const rect = el.getBoundingClientRect();
        return rect.top < window.innerHeight && rect.bottom > 0;
      });
      setIsFooterInView(overlapsLegal);
    };

    syncFooterState();
    window.addEventListener('scroll', syncFooterState, { passive: true });
    window.addEventListener('resize', syncFooterState);

    return () => {
      window.removeEventListener('scroll', syncFooterState);
      window.removeEventListener('resize', syncFooterState);
    };
  }, [isHydrated, isEditorialReskin, isMobile]);

  const shouldShowMobileBottomBar =
    showMobileBottomBar && isMobile && !(isEditorialReskin && isFooterInView);

  if (!isHydrated) return null;

  return (
    <LayoutContainer
      id={sectionId || undefined}
      data-cy="builder-classic-two-column-layout"
      ref={layoutRef}
      sx={{
        // se3: force transparent so the cream page background shows through
        // uniformly (the author-set near-white prop otherwise creates a seam
        // against the editorial hero/nav cream).
        backgroundColor: isEditorialReskin ? 'transparent' : backgroundColor,
        px: `${horizontalPadding}px`,
        py: `${verticalPadding}px`,
        // clip: false — the right column hosts a position: sticky sidebar.
        // The default clip: true adds `overflow: hidden`, which would make
        // this container the sticky's scroll ancestor and effectively kill
        // stickiness (the sidebar scrolls away with the page instead of
        // pinning). Documented in `shared/sectionBorderRadius.js`.
        ...sectionBorderRadiusSx(sectionBorderRadius, { clip: false }),
      }}
    >
      <ContentWrapper sx={{ maxWidth: `${clampedMaxWidth}px` }}>
        <Grid container sx={{ overflow: 'visible', alignItems: 'stretch' }}>
          <LeftColumn item xs={12} lg={leftColumnRatio} className="left" sx={{ mt: '16px' }}>
            {isMobile && showStats && (
              <Box
                sx={{ mb: 2, display: { xs: 'block', lg: 'none' } }}
                data-cy="mobile-inline-stats"
              >
                <BuilderClassicInvestSidebar
                  showLoyaltyBonus={showLoyaltyBonus}
                  showStats={showStats}
                  showRequestLiveStream={showRequestLiveStream}
                  showInvestButton={false}
                  investButtonColor={investButtonColor}
                  waitlistButtonColor={waitlistButtonColor}
                  forceReserveCta={forceReserveCta}
                />
              </Box>
            )}
            <BuilderBlocks
              parentElementId={builderBlock?.id}
              dataPath="component.options.leftContent"
              blocks={leftContent}
            />
          </LeftColumn>

          <RightColumn
            item
            xs={0}
            lg={rightColumnRatio}
            className="right"
            ref={rightColRef}
            sx={{ position: 'relative' }}
          >
            <Box
              ref={sidebarRef}
              data-cy="builder-classic-sticky-sidebar"
              sx={{
                // !important mirrors the legacy non-Builder `Body.jsx`
                // sidebar — defends against MUI/page-level rules that
                // sometimes promote `position` and break sticky pinning
                // (PD-000 / matches existing CompanyComps behavior).
                position: 'sticky !important',
                top: `${stickyTopOffset}px !important`,
                width: `${sidebarWidth}px`,
                marginLeft: 0,
                zIndex: 999,
              }}
            >
              <BuilderClassicInvestSidebar
                showLoyaltyBonus={showLoyaltyBonus}
                showStats={showStats}
                showRequestLiveStream={showRequestLiveStream}
                investButtonColor={investButtonColor}
                waitlistButtonColor={waitlistButtonColor}
                forceReserveCta={forceReserveCta}
              />
            </Box>
          </RightColumn>
        </Grid>
      </ContentWrapper>

      {shouldShowMobileBottomBar && (
        <MobileBottomBar data-cy="classic-mobile-bottom-bar">
          <MobileBarContent
            investButtonColor={investButtonColor}
            waitlistButtonColor={waitlistButtonColor}
            forceReserveCta={forceReserveCta}
          />
        </MobileBottomBar>
      )}
    </LayoutContainer>
  );
};

export default BuilderClassicTwoColumnLayout;
